import { CheckCircleIcon, ArrowRightIcon, SparklesIcon, ArrowUpIcon } from "@heroicons/react/24/solid";
import { motion } from "framer-motion";
import React, { useState, useEffect } from 'react';

const INFLUENTIAL_FACTORS = [
  {
    title: "Have Hobbies and Understand Importance",
    description: "Engaging in activities outside of work for personal fulfillment and balance",
    questions: [
      "I take time for hobbies or activities I enjoy.",
      "I have enough time outside of work to enjoy my hobbies or interests."
    ],
    action: "Schedule dedicated time each week for your hobbies and interests"
  }
];

export default function HobbiesAndInterests() {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show button when user is near bottom (within 100px)
      setShowScrollButton(documentHeight - scrollPosition < 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
      {/* Hero Section */}
      <div className="lg:col-span-12">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-2xl" />
          <div className="relative p-4 lg:p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center space-x-2 bg-amber-100 px-3 lg:px-4 py-2 rounded-full mb-4"
            >
              <SparklesIcon className="h-4 w-4 lg:h-5 lg:w-5 text-amber-600" />
              <span className="text-amber-600 font-medium text-sm lg:text-base">Domain Deep Dive</span>
            </motion.div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 lg:mb-4">Hobbies & Interests</h1>
            <p className="text-base lg:text-lg text-gray-600">
              Hobbies and interests are essential for maintaining a healthy work-life balance and personal fulfillment. They provide
              opportunities for relaxation, creativity, and personal growth outside of work. Engaging in activities you enjoy can
              reduce stress, boost energy, and bring more joy to your life.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="lg:col-span-8 space-y-4 lg:space-y-8">
        {/* Action Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">How to Cultivate Hobbies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            {[
              {
                title: "Make Time",
                description: "Schedule regular time for your hobbies and interests."
              },
              {
                title: "Try New Things",
                description: "Explore different activities to find what brings you joy."
              },
              {
                title: "Set Boundaries",
                description: "Create clear separation between work and personal time."
              },
              {
                title: "Share Interests",
                description: "Connect with others who share your hobbies."
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-x-3 p-3 rounded-xl hover:bg-amber-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-amber-600 text-sm font-semibold">{index + 1}</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{step.title}</h3>
                  <p className="mt-1 text-gray-600 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Reflection Questions */}
        <div className="bg-amber-50 rounded-2xl p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">Reflection Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
            {[
              "What activities bring me joy?",
              "How can I make more time for hobbies?",
              "What new interests would I like to explore?",
              "How do my hobbies contribute to my well-being?",
              "What boundaries do I need to set?",
              "How can I share my interests with others?"
            ].map((question, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-3 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-x-2">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                      <span className="text-amber-600 text-xs">?</span>
                    </div>
                  </div>
                  <p className="text-gray-900 text-sm">{question}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar Content */}
      <div className="lg:col-span-4 space-y-4 lg:space-y-8">
        {/* Self-Assessment */}
        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">Influential Factors</h2>
          <div className="space-y-6 lg:space-y-8">
            {INFLUENTIAL_FACTORS.map((factor, index) => (
              <div key={index} className="space-y-3 lg:space-y-4">
                <div>
                  <h3 className="text-base lg:text-lg font-medium text-gray-900">{factor.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{factor.description}</p>
                </div>
                
                <div className="space-y-3">
                  {factor.questions.map((question, qIndex) => (
                    <div key={qIndex} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{question}</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-2 bg-amber-50 p-3 rounded-lg">
                  <SparklesIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Action Item</p>
                    <p className="text-sm text-amber-700 mt-1">{factor.action}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: showScrollButton ? 1 : 0, y: showScrollButton ? 0 : 20 }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 bg-amber-600 text-white p-3 rounded-full shadow-lg hover:bg-amber-700 transition-colors ${
          showScrollButton ? 'block' : 'hidden'
        }`}
      >
        <ArrowUpIcon className="h-6 w-6" />
      </motion.button>
    </div>
  );
}
